/**
 * ─────────────────────────────────────────────
 * src/lib/api.js
 * ─────────────────────────────────────────────
 *
 * WHY THIS FILE EXISTS:
 * Instead of writing fetch('/api/auth/login', { headers: {...} }) in every
 * component, we create ONE axios "instance" with shared config.
 * Every API call in the app uses this instance.
 *
 * WHAT IS AN AXIOS INSTANCE?
 * Think of it as a pre-configured HTTP client.
 * Like a template: baseURL is already set, headers are already set.
 * You just call api.post('/auth/login', data) and it handles the rest.
 *
 * WHAT IS AN INTERCEPTOR?
 * A function that runs BEFORE every request or AFTER every response.
 * - Request interceptor: "Before you send this request, add the token"
 * - Response interceptor: "If you get a 401, log the user out"
 */

import axios from "axios";

// ─────────────────────────────────────────────
// Resolve the API base URL.
//
// CRA inlines REACT_APP_* at BUILD time, so whatever .env holds when `npm run
// build` runs is baked into the bundle. If that value is a localhost URL, a
// deployed site asks the *visitor's* machine for the API and every request
// fails — which surfaces as empty data and blank panels.
//
// So: only trust REACT_APP_API_URL when it isn't a localhost address while we
// are served from a real domain. Otherwise fall back to the site's own origin
// (`/api`), which works behind the usual reverse-proxy/rewrite setup.
// ─────────────────────────────────────────────
const isLocalHostname = (host) =>
  ["localhost", "127.0.0.1", "0.0.0.0", "[::1]"].includes(host);

// Always end the base at exactly one "/api", whether the env var was written
// as "https://host", "https://host/", or "https://host/api". Callers of the
// axios instance pass paths without the prefix ("/auth/login"), so a missing
// or duplicated "/api" breaks every request.
const normalizeBase = (value) => {
  const trimmed = String(value).replace(/\/+$/, "");
  return /\/api$/i.test(trimmed) ? trimmed : `${trimmed}/api`;
};

// "localhost" can resolve to IPv6 ::1 first. If another dev server (e.g. a
// Next.js app) holds port 3000 on ::1 while the backend listens on IPv4, every
// API call silently reaches the wrong app and gets HTML back. Talk to the
// backend on 127.0.0.1 explicitly.
const forceIPv4Loopback = (value) =>
  String(value).replace(/^(https?:\/\/)localhost(?=[:/]|$)/i, "$1127.0.0.1");

const resolveApiBase = () => {
  const configured = process.env.REACT_APP_API_URL
    ? forceIPv4Loopback(process.env.REACT_APP_API_URL)
    : process.env.REACT_APP_API_URL;
  const servedLocally =
    typeof window !== "undefined" && isLocalHostname(window.location.hostname);

  if (configured) {
    const pointsAtLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])/i.test(
      configured,
    );
    // A localhost API is only usable when the page itself is local.
    if (!pointsAtLocalhost || servedLocally) return normalizeBase(configured);
  }

  if (servedLocally) return "http://127.0.0.1:3000/api";

  // Deployed: use this site's own origin so the host's proxy/rewrite handles it.
  return "/api";
};

const API_BASE = resolveApiBase();

// ─────────────────────────────────────────────
// Create the axios instance
// baseURL: all requests will be prefixed with this
// So api.post('/auth/login') → POST <API_BASE>/auth/login
// ─────────────────────────────────────────────
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  // 10s is fine on localhost but too tight in production: the Reports pages
  // run heavy aggregations, and a hosted database adds network latency on top.
  // A timeout here surfaces as a blank/failed page, so allow more headroom.
  timeout: Number(process.env.REACT_APP_API_TIMEOUT_MS) || 30000,
});

// ─────────────────────────────────────────────
// REQUEST INTERCEPTOR
// Runs before EVERY request this axios instance makes.
//
// WHY: Some routes need Authorization header (protected routes).
//      Instead of adding the token manually in every API call,
//      this interceptor automatically reads from localStorage and adds it.
//
// Example without interceptor (bad — repeated everywhere):
//   axios.get('/dashboard', { headers: { Authorization: `Bearer ${token}` } })
//
// Example with interceptor (good — done once here):
//   api.get('/dashboard')  ← token is added automatically
// ─────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      // Attach token to every request if it exists
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config; // Must return config — axios needs it to proceed
  },
  (error) => {
    // If there's an error BUILDING the request (rare), reject it
    return Promise.reject(error);
  },
);

// ─────────────────────────────────────────────
// RESPONSE INTERCEPTOR
// Runs after EVERY response comes back.
//
// WHY: Handle global errors in one place.
//      If the server returns 401 (Unauthorized / token expired),
//      we automatically clear localStorage and redirect to login.
//      This prevents a user with an expired token from being "stuck".
// ─────────────────────────────────────────────
// Sliding sessions: while the user is active the server returns a fresh,
// short-lived token in this header. Keep it, so the session only ends after
// a period of inactivity rather than at a fixed time.
// Only ever move forward: a refreshed token is kept if it is newer than the one
// held and not yet expired. A response replayed from the browser cache can
// carry an old header, which must never replace a live session.
const readClaims = (token) => {
  try {
    return JSON.parse(
      atob(String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
  } catch {
    return null;
  }
};

const storeRefreshedToken = (getHeader) => {
  const fresh = getHeader("x-refreshed-token");
  const current = localStorage.getItem("token");
  if (!fresh || !current || fresh === current) return;

  const freshClaims = readClaims(fresh);
  const currentClaims = readClaims(current);
  if (!freshClaims?.iat || !freshClaims?.exp) return;
  if (freshClaims.exp * 1000 <= Date.now()) return;
  if (currentClaims?.iat && freshClaims.iat <= currentClaims.iat) return;

  localStorage.setItem("token", fresh);
};

const AUTH_PAGES = [
  "/",
  "/forgot-password",
  "/otp",
  "/reset-password",
  "/set-password",
];

// A 401 ends the session only when it was earned by the token we hold now.
// A request sent with an older token — from another tab, or one still in
// flight across a re-login — must not sign out the newer session.
// The reason is kept in localStorage ("qh_last_signout") and logged, so an
// unexpected sign-out can be diagnosed.
// Non-secret facts about a token (never the token itself), for the sign-out log.
const describeToken = (token) => {
  if (!token) return null;
  try {
    const claims = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return {
      issuedAt: new Date(claims.iat * 1000).toISOString(),
      expiresAt: new Date(claims.exp * 1000).toISOString(),
      role: claims.role,
    };
  } catch {
    return { unreadable: String(token).slice(0, 12) };
  }
};

const signOutOnUnauthorized = ({ sentAuthHeader, url, message, detail }) => {
  const current = localStorage.getItem("token");
  const sentToken = String(sentAuthHeader || "").replace(/^Bearer\s+/i, "") || null;
  if (sentToken && current && sentToken !== current) return;
  if (AUTH_PAGES.includes(window.location.pathname)) return;

  const reason = {
    at: new Date().toISOString(),
    page: window.location.pathname,
    request: url,
    apiBase: API_BASE,
    message: message || null,
    detail: detail || null,
    sentToken: describeToken(sentToken),
    sameAsStored: sentToken === current,
  };
  try {
    localStorage.setItem("qh_last_signout", JSON.stringify(reason));
    const history = JSON.parse(localStorage.getItem("qh_signout_log") || "[]");
    localStorage.setItem(
      "qh_signout_log",
      JSON.stringify([reason, ...history].slice(0, 20)),
    );
  } catch {}
  console.warn("[auth] Signed out:", reason);

  localStorage.removeItem("token");
  localStorage.removeItem("user");
  window.location.href = "/";
};

api.interceptors.response.use(
  (response) => {
    storeRefreshedToken((name) => response.headers?.[name]);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      signOutOnUnauthorized({
        sentAuthHeader: error.config?.headers?.Authorization,
        url: error.config?.url,
        message: error.response?.data?.message,
        detail: error.response?.data?.detail,
      });
    }

    // Give the failures that look identical to "the page is broken" a real
    // message, so a rate-limited or slow response is diagnosable instead of
    // rendering as an empty section.
    if (error.response?.status === 429) {
      error.message =
        "Too many requests. Please wait a moment and refresh the page.";
    } else if (error.code === "ECONNABORTED") {
      error.message =
        "The server took too long to respond. Please refresh and try again.";
    } else if (!error.response) {
      error.message =
        "Unable to reach the server. Check your connection and try again.";
    }

    return Promise.reject(error);
  },
);

export default api;

// ─────────────────────────────────────────────
// Keep legacy helpers so existing imports don't break
// ─────────────────────────────────────────────

// authFetch callers pass paths that already include the "/api" prefix
// (e.g. "/api/leaves/apply"), while REACT_APP_API_URL may or may not end in
// "/api" depending on how it was configured. Strip the suffix from the base
// so the two never concatenate into "/api/api/...", which 404s.
//
// Derived from the same resolution as the axios instance, so authFetch and
// api never disagree about which backend they are talking to.
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, "");

const resolveApiUrl = (url) => {
  if (url.startsWith("http")) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  const joined = `${API_ORIGIN}${path}`;
  // Belt-and-braces: collapse an accidental duplicate prefix so a mismatched
  // REACT_APP_API_URL can never produce "/api/api/...".
  return joined.replace(/([^:]\/)api\/api\//, "$1api/");
};

// Mirror the axios 401 policy: expired/invalid token logs the user out
// instead of leaving pages silently empty.
const handleAuthExpiry = async (res, sentToken) => {
  storeRefreshedToken((name) => res.headers.get(name));
  if (res.status === 401) {
    const body = await res.clone().json().catch(() => ({}));
    signOutOnUnauthorized({
      sentAuthHeader: sentToken,
      url: res.url,
      message: body.message,
      detail: body.detail,
    });
  }
  return res;
};

// fetch() has no built-in timeout: without this, a stalled request on a slow
// production connection leaves the Timesheet and notification panels spinning
// forever with no error. AbortSignal.timeout gives them the same ceiling the
// axios instance uses.
const REQUEST_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 30000;

const withTimeout = (options) => {
  // Respect a caller-supplied signal rather than overriding it.
  if (options.signal || typeof AbortSignal?.timeout !== "function") {
    return options;
  }
  return { ...options, signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) };
};

export const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("token");
  return fetch(resolveApiUrl(url), {
    ...withTimeout(options),
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  }).then((res) => handleAuthExpiry(res, token));
};

export const authFetchFormData = (url, options = {}) => {
  const token = localStorage.getItem("token");
  return fetch(resolveApiUrl(url), {
    // File uploads are legitimately slow, so they get a longer ceiling than
    // ordinary JSON requests — but still not unbounded.
    ...(options.signal || typeof AbortSignal?.timeout !== "function"
      ? options
      : { ...options, signal: AbortSignal.timeout(120000) }),
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  }).then((res) => handleAuthExpiry(res, token));
};

export const safeJsonParse = (str, fallback = null) => {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};
